import classnames from 'classnames/bind'
import { useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { WbProductVariant } from '~/@types/product'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'
import { processCartItems } from '~/lib/process-cart-items'

import {
  Accordion,
  AccordionPanel,
  ProductDataProps,
} from '@unlikelystudio/react-abstract-components'

import AddToCartButton from '~/components/Abstracts/AddToCartButton'
import PopIn from '~/components/Abstracts/PopIn'
import { useNavBannerContext } from '~/components/Navigation/NavigationBanner'
import AlertStockPopin, {
  AlertStockPopinProps,
} from '~/components/Popins/AlertStockPopin'
import ContactPopin from '~/components/Popins/ContactPopin'
import Breadcrumb from '~/components/UI/Breadcrumb'
import { CloseIcon, DownIcon } from '~/components/UI/Icons'
import { ImageProps } from '~/components/UI/Image'
import InlineCta from '~/components/UI/InlineCta'
import ProductHeaderVariants from '~/components/UI/ProductHeader/ProductHeaderVariants'
import { ProductSizeGuideProps } from '~/components/UI/ProductHeader/ProductSizeGuide'
import ProductReassurance from '~/components/UI/ProductReassurance'
import ReadMoreText from '~/components/UI/ReadMoreText'
import RichText, { RichTextBlock } from '~/components/UI/RichText'
import SquaredCta from '~/components/UI/SquaredCta'
import WishlistIcon from '~/components/UI/WishlistIcon'

import { useGlobalData } from '~/providers/GlobalDataProvider'
import { usePopIn } from '~/providers/PopInProvider'
import { useSettings } from '~/providers/SettingsProvider'
import { useStyle } from '~/providers/StyleProvider'

import useLang from '~/hooks/useLang'
import useMarket from '~/hooks/useMarket'

import { CART, CTA, PRODUCT } from '~/data/dictionary'
import { ProductVariations } from '~/data/product-page-data/serialize-product-header'
import { SerializedProduct } from '~/data/serialize-product'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type ProductCustomState = {
  selectedSize: string
  isUniqueSize: boolean
  isPreOrder: boolean
  isAdjusting: boolean
}

const defaultProductCustomState = {
  selectedSize: null,
  isUniqueSize: false,
  isPreOrder: false,
  isAdjusting: false,
}

export type ProductPreOrderProps = {
  isActive?: boolean
  deliveryDate?: string
}

export type ProductDetailsAccordionItemsProps = {
  headline?: string
  text?: RichTextBlock
}
export type ProductDetailsAccordionProps = {
  className?: string
  theme?: typeof GlobalThemeColors.Black | typeof GlobalThemeColors.White
  items: ProductDetailsAccordionItemsProps[]
}
export type ProductDetailsProps = SerializedProduct & {
  className?: string
  uuid?: string
  defaultVariant?: WbProductVariant
  selectedVariant?: WbProductVariant
  setSelectedVariant?(variant: WbProductVariant): void
  description?: string
  reference?: string
  sections?: ProductDetailsAccordionItemsProps[]
  sizeGuide?: ProductSizeGuideProps
  isQuickbuy?: boolean
  isFooterVisible?: boolean
  onAddToCart?(): void
  jsonLD?: ProductDataProps
  preOrder?: ProductPreOrderProps
  isStockAlert?: boolean
  isSpecialOrder?: boolean
  isAdjusting?: boolean
  adjustableOffset?: number
  productVariations?: ProductVariations
  stockAlertProps?: AlertStockPopinProps
  firstImage?: ImageProps
}

function ProductDetailsAccordion({
  className,
  items,
}: ProductDetailsAccordionProps) {
  const headlineStyle = useStyle({
    textPreset: GlobalTextPreset.Label11GroteskRegular,
    textStyling: GlobalTextStyling.UpperCase,
  })

  const bodyStyle = useStyle({
    textPreset: GlobalTextPreset.Text12GroteskRegular,
  })

  const t = useTranslate()

  return (
    <>
      {items?.length > 0 && (
        <Accordion className={cx(css.accordion, className)}>
          {items?.map(({ headline, text }, key) => (
            <AccordionPanel
              key={`panel_${key}`}
              className={cx(css.panel)}
              index={key}
              head={(isActive: boolean) => (
                <div
                  className={cx(css.panelHead, {
                    isActive,
                  })}>
                  <p className={cx(css.title, headlineStyle)}>{t(headline)}</p>

                  <DownIcon className={cx(css.icon, { isActive })} />
                </div>
              )}
              body={
                <div className={cx(css.panelBody, bodyStyle)}>
                  <RichText className={cx(css.text)} render={text} />
                </div>
              }
            />
          ))}
        </Accordion>
      )}
    </>
  )
}

function ProductDetails({
  className,
  slug,
  shopifyId,
  cardImage,
  productName,
  productDesigner,
  variants,
  selectedVariant,
  sizeGuide,
  reference,
  description,
  sections,
  isQuickbuy,
  setSelectedVariant,
  onAddToCart,
  trackingData,
  isAdjusting,
  adjustableOffset,
  isStockAlert,
  isSpecialOrder,
  preOrder,
  link,
  productVariations,
  stockAlertProps,
  firstImage,
  uuid,
}: ProductDetailsProps) {
  const market = useMarket()
  const [isNavBannerVisible] = useNavBannerContext()
  const { wishlistEnabled, stockAlertEnabled } = useSettings()
  const { global } = useGlobalData()
  const lang = useLang()
  const productAvailableForSale =
    selectedVariant?.availableForSale && !selectedVariant?.currentlyNotInStock

  const enablePreOrder =
    selectedVariant?.currentlyNotInStock && preOrder?.isActive

  const isOutOfSkock = !productAvailableForSale && !enablePreOrder

  const [customProductState, setCustomProductState] =
    useState<ProductCustomState>(defaultProductCustomState)

  const [quantity] = useState(1)

  const t = useTranslate()
  const { add: addPopin, removeCurrent } = usePopIn()

  const titleStyle = useStyle({
    textPreset: GlobalTextPreset.Label12Grotesk,
  })
  const textStyle = useStyle({
    textPreset: GlobalTextPreset.Text12GroteskRegular,
  })

  const compareAtStyle = useStyle({
    textPreset: GlobalTextPreset.Text12GroteskRegular,
    color: GlobalThemeColors.Gray,
  })

  const addToCartActions = () => {
    removeCurrent()
    onAddToCart?.()
  }

  const cartItems = processCartItems({
    merchandiseId: selectedVariant?.id,
    quantity,
    attributes: {
      lang,
      slug,
      name: productName,
      designer: productDesigner?.children?.toString() ?? null,
      reference: reference ?? null,
      image: cardImage?.src ?? firstImage?.src ?? null,
      shopifyId,
      trackingData:
        JSON.stringify(trackingData, (key, value) => {
          return typeof value === 'string'
            ? encodeURI(value)?.replace(/'/g, '`')
            : value
        }) ?? '',
      ...customProductState,
    },
  })

  const { ref: inViewTopMarkerRef } = useInView({
    threshold: 0.2,
    initialInView: true,
  })

  const showStockAlertPopin = () =>
    addPopin(
      <PopIn
        className={cx(css.popin)}
        isCloseButton={true}
        closeButton={<CloseIcon className={css.alertStockIconClose} />}>
        <AlertStockPopin
          className={cx(css.alertStock)}
          variantId={selectedVariant?.id}
          productId={shopifyId}
          {...stockAlertProps}
        />
      </PopIn>,
    )

  const showContactPopin = () =>
    addPopin(
      <PopIn
        className={cx(css.popin)}
        isCloseButton={true}
        closeButton={<CloseIcon className={css.alertStockIconClose} />}>
        <ContactPopin className={cx(css.contactPopin)} />
      </PopIn>,
    )

  const renderCtas = (isStockAlert, isSpecialOrder, selectedVariant) => {
    if (isStockAlert && stockAlertEnabled && !productAvailableForSale) {
      return (
        <SquaredCta
          className={cx(css.addToCart, css.isOutOfStock)}
          theme={GlobalThemeColors.Black}
          textPreset={GlobalTextPreset.Cta12Grotesk}
          onClick={showStockAlertPopin}>
          {t(CTA.QUICKBUY_STOCK_ALERT)}
        </SquaredCta>
      )
    }

    if (isSpecialOrder && !productAvailableForSale) {
      return (
        <SquaredCta
          className={cx(css.addToCart, css.isOutOfStock)}
          theme={GlobalThemeColors.Black}
          textPreset={GlobalTextPreset.Cta12Grotesk}
          onClick={showContactPopin}>
          {t(CTA.SPECIAL_ORDER)}
        </SquaredCta>
      )
    }

    return (
      <AddToCartButton
        className={cx(css.addToCart)}
        onClick={addToCartActions}
        isOutOfStock={selectedVariant && isOutOfSkock}
        isPreOrder={enablePreOrder}
        cartItems={cartItems}
        theme={GlobalThemeColors.Black}
      />
    )
  }

  const renderSelectors = (className?, theme?) => (
    <>
      <ProductHeaderVariants
        className={className}
        variants={variants}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        theme={theme ?? GlobalThemeColors.Black}
        notes={global?.product?.notes}
        preOrder={enablePreOrder && preOrder}
        isSpecialOrder={isSpecialOrder}
        isStockAlert={isStockAlert}
        isQuickbuy={isQuickbuy}
        isAdjusting={isAdjusting}
        adjustableOffset={adjustableOffset}
        hasSizeGuide={!!sizeGuide}
        sizeGuide={sizeGuide}
        customProductState={customProductState}
        setCustomProductState={setCustomProductState}
        productVariations={productVariations}
      />
      <div className={cx(css.cartContainer)}>
        {renderCtas(isStockAlert, isSpecialOrder, selectedVariant)}
        {wishlistEnabled && (
          <WishlistIcon
            className={css.wishlistIcon}
            trackingData={trackingData}
            shopifyId={shopifyId}
            variantId={selectedVariant?.id}
            productLink={link}
            uuid={uuid}
          />
        )}
      </div>
    </>
  )

  // Rerendering sections depending on the shops availability for the selected variant
  const sectionsToRender = [
    ...(selectedVariant?.shops && selectedVariant?.shops?.length > 0
      ? selectedVariant.shops
      : []),
    ...(sections && sections?.length > 0 ? sections : []),
  ] as ProductDetailsAccordionItemsProps[]

  const vatLabel =
    market === 'UK'
      ? t(PRODUCT.TAXES_UK, { _: 'VAT included' })
      : market === 'US'
      ? t(PRODUCT.TAXES_US, { _: 'taxes & duties included' })
      : market === 'EU'
      ? t(CART.TAXES_INCLUDED)
      : t(CART.TAXES_EXCLUDED)

  const vatStyle = useStyle({
    textPreset: GlobalTextPreset.Text12GroteskRegular,
  })

  return (
    <div
      className={cx(css.ProductDetails, className, {
        isNavBannerVisible,
      })}>
      <div className={css.inViewMarker} ref={inViewTopMarkerRef} />
      <Breadcrumb
        className={css.breadcrumb}
        theme={'product'}
        color={GlobalThemeColors.Gray}
        textPreset={GlobalTextPreset.Cta12GroteskRegular}
        textStyling={GlobalTextStyling.Capitalize}
      />
      <div className={cx(css.wrapper)}>
        <div className={css.top}>
          {productDesigner && (
            <InlineCta
              textPreset={GlobalTextPreset.Title20_24SangBleu}
              textStyling={GlobalTextStyling.None}
              lineProps={{ initialLineState: 'none' }}
              className={cx(css.link)}
              {...productDesigner}>
              <h2>{productDesigner?.children}</h2>
            </InlineCta>
          )}
          <div className={css.topInfos}>
            <h1 className={cx(css.title, titleStyle)}>{productName}</h1>
            <span className={cx(css.price, titleStyle)}>
              <span className={cx(css.vat, vatStyle)}>{vatLabel}</span>
              {selectedVariant?.compareAtPrice && (
                <del className={cx(css.compareAtPrice, compareAtStyle)}>
                  {selectedVariant?.compareAtPrice}
                </del>
              )}
              {selectedVariant?.price}
            </span>
          </div>
        </div>

        <div className={css.body}>
          <div className={cx(css.ctasContainer)}>
            {renderSelectors(css.mainSelector)}
          </div>
        </div>

        <div className={css.bottom}>
          {description && (
            <ReadMoreText
              className={css.description}
              charsLimit={150}
              text={description}
            />
          )}
          {reference && (
            <p className={cx(css.reference, textStyle)}>{reference}</p>
          )}
          {sectionsToRender && (
            <ProductDetailsAccordion
              className={css.sections}
              items={sectionsToRender}
            />
          )}
          {global?.product?.reassurance && (
            <ProductReassurance
              className={css.reassurance}
              items={global?.product?.reassurance}
            />
          )}
        </div>
      </div>
    </div>
  )
}

ProductDetails.defaultProps = {}

export default ProductDetails
